/**
 * Drishti station constants that match the backend station_config CHECK constraints.
 */
export interface StationKey {
    readonly site: string;
    readonly stationNumber: string;
}

export const STATION_TYPE = {
    UNIVERSAL: 'UNIVERSAL',
    ARSAW: 'ARSAW',
    ARSTOW: 'ARSTOW',
} as const;
export type StationType = (typeof STATION_TYPE)[keyof typeof STATION_TYPE];

export const ORIENTATION = {
    LEFT: 'LEFT',
    RIGHT: 'RIGHT',
} as const;
export type Orientation = (typeof ORIENTATION)[keyof typeof ORIENTATION];

export const STATION_TYPE_LABELS: Record<StationType, string> = {
    [STATION_TYPE.UNIVERSAL]: 'Universal',
    [STATION_TYPE.ARSAW]: 'ARSAW',
    [STATION_TYPE.ARSTOW]: 'ARSTOW',
};

export const CAMERA_COUNTS = [3, 4] as const;
export type CameraCount = (typeof CAMERA_COUNTS)[number];

export interface StationConfig {
    readonly stationType: StationType;
    readonly orientation: Orientation;
    readonly cameraCount: CameraCount;
}

type StationConfigField = keyof StationConfig;

export type StationConfigDraft = { readonly [K in keyof StationConfig]: StationConfig[K] | null };

export const BLANK_STATION_CONFIG: StationConfigDraft = {
    stationType: null,
    orientation: null,
    cameraCount: null,
};

const STATION_CONFIG_FIELDS = Object.keys(BLANK_STATION_CONFIG) as readonly StationConfigField[];

export const isCompleteStationConfig = (draft: StationConfigDraft): draft is StationConfig =>
    STATION_CONFIG_FIELDS.every((field) => draft[field] != null);

const isOneOf = <T>(values: readonly T[], value: unknown): value is T => (values as readonly unknown[]).includes(value);

export const toStationConfig = (body: unknown): StationConfig | null => {
    if (typeof body !== 'object' || body === null) {
        return null;
    }
    const { stationType, orientation, cameraCount } = body as Record<StationConfigField, unknown>;
    return isOneOf(Object.values(STATION_TYPE), stationType) &&
        isOneOf(Object.values(ORIENTATION), orientation) &&
        isOneOf(CAMERA_COUNTS, cameraCount)
        ? { stationType, orientation, cameraCount }
        : null;
};

interface StationConfigFact {
    readonly label: string;
    readonly value: string;
}

// Orientation is resolved and kept in state, but never shown: the installer has no use for it.
const STATION_CONFIG_FACTS: readonly { label: string; format: (config: StationConfig) => string }[] = [
    { label: 'Station type', format: ({ stationType }) => STATION_TYPE_LABELS[stationType] },
    { label: 'Camera count', format: ({ cameraCount }) => String(cameraCount) },
];

export const describeStationConfig = (config: StationConfig): readonly StationConfigFact[] =>
    STATION_CONFIG_FACTS.map(({ label, format }) => ({ label, value: format(config) }));

export const STATION_NUMBER_LENGTH = 4;

const STATION_NUMBER_PATTERN = new RegExp(`^[0-9]{${STATION_NUMBER_LENGTH}}$`);

export const isStationNumber = (value: string): boolean => STATION_NUMBER_PATTERN.test(value);

export const formatStation = ({ site, stationNumber }: StationKey): string => `${site}-${stationNumber}`;

export const MENTAT_SERIAL_PREFIX = 'GT54VEA';
export const MENTAT_SERIAL_LENGTH = 16;

export const MENTAT_SERIAL_SUFFIX_LENGTH = MENTAT_SERIAL_LENGTH - MENTAT_SERIAL_PREFIX.length;

// TODO: restore the two commented-out prefix checks once lab testing no longer needs other serials.
// const MENTAT_SERIAL_PATTERN = new RegExp(`^${MENTAT_SERIAL_PREFIX}[0-9A-Z]{${MENTAT_SERIAL_SUFFIX_LENGTH}}$`);
const MENTAT_SERIAL_PATTERN = new RegExp(`^[0-9A-Z]{${MENTAT_SERIAL_LENGTH}}$`);
const MENTAT_SERIAL_CHARACTER_PATTERN = /^[0-9A-Z]*$/;

export const isMentatSerial = (serial: string): boolean => MENTAT_SERIAL_PATTERN.test(serial);

// export const isPartialMentatSerial = (serial: string): boolean =>
//     serial.length < MENTAT_SERIAL_LENGTH &&
//     (MENTAT_SERIAL_PREFIX.startsWith(serial) ||
//         (serial.startsWith(MENTAT_SERIAL_PREFIX) &&
//             MENTAT_SERIAL_CHARACTER_PATTERN.test(serial.slice(MENTAT_SERIAL_PREFIX.length))));
export const isPartialMentatSerial = (serial: string): boolean =>
    serial.length < MENTAT_SERIAL_LENGTH && MENTAT_SERIAL_CHARACTER_PATTERN.test(serial);
